import { useCallback, useRef } from 'react';
import { CallRequestType } from '../customer-call-sdk/types/call';
import { parseExternalMeetingUrl } from '../services/parse-meeting-details.service';
import { MeetingDetails } from '../types/meeting-details';
import { isPotentialMeetingMessage, toHandoffCallType } from '../utils/meeting-details.util';

const ATTEMPT_WINDOW_MS = 60000;
const ATTEMPT_WINDOW_MAX_MESSAGES = 3;

export interface UseParseMeetingDetailsOptions {
    requestId: string | null;
    requestType: CallRequestType;
}

export interface UseParseMeetingDetailsResult {
    inspectOutgoingMessage: (text: string) => Promise<MeetingDetails | null>;
}

export default function useParseMeetingDetails({
    requestId,
    requestType,
}: UseParseMeetingDetailsOptions): UseParseMeetingDetailsResult {
    const attemptWindowRef = useRef({ messages: 0, startedAt: 0, hasAttempt: false });
    const attemptCountRef = useRef(0);

    const advanceWindow = useCallback((): void => {
        const now = Date.now();
        const attemptWindow = attemptWindowRef.current;

        if (
            attemptWindow.messages >= ATTEMPT_WINDOW_MAX_MESSAGES ||
            now - attemptWindow.startedAt > ATTEMPT_WINDOW_MS
        ) {
            attemptWindow.messages = 0;
            attemptWindow.startedAt = now;
            attemptWindow.hasAttempt = false;
        }

        attemptWindow.messages += 1;
    }, []);

    const claimAttemptIndex = useCallback((): number | null => {
        const attemptWindow = attemptWindowRef.current;

        if (attemptWindow.hasAttempt) {
            return null;
        }

        attemptWindow.hasAttempt = true;
        attemptCountRef.current += 1;
        return attemptCountRef.current;
    }, []);

    const inspectOutgoingMessage = useCallback(
        async (text: string): Promise<MeetingDetails | null> => {
            advanceWindow();

            try {
                const callType = toHandoffCallType(requestType);
                if (!callType || !requestId) {
                    return null;
                }

                if (attemptWindowRef.current.hasAttempt) {
                    return null;
                }

                if (!isPotentialMeetingMessage(text)) {
                    return null;
                }

                const { data } = await parseExternalMeetingUrl(text);
                if (!data) {
                    return null;
                }

                const attemptIndex = claimAttemptIndex();
                console.log('attemptIndex', attemptIndex);
                if (attemptIndex === null) {
                    return null;
                }

                return { ...data, requestId, callType, attemptIndex };
            } catch {
                return null;
            }
        },
        [requestId, requestType, advanceWindow, claimAttemptIndex],
    );

    return { inspectOutgoingMessage };
}
